import React, { createContext, useContext } from 'react';
import { usePerformedSearchRequest } from 'clientState/providers/SearchRequestProvider';
import { useFilterState } from './StateProvider';
import { DONT_CARE_OPTION } from '../queries/Collector';
import {
  PredicateContextType,
  SegmentPredicate,
  ItineraryPredicate,
  PredicateProviderProps,
} from './types';

const PredicateContext = createContext<PredicateContextType>(() => true);

const alwaysTrue = () => true;

const createSegmentPredicate =
  (selectedConnectionThreshold: string): SegmentPredicate =>
  (segment) => {
    const numberOfConnections = segment.sections.length - 1;
    return (
      selectedConnectionThreshold === DONT_CARE_OPTION ||
      Number(selectedConnectionThreshold) >= numberOfConnections
    );
  };

const createRoundTripSegmentPredicates = (
  selectedConnectionThreshold: string
): SegmentPredicate[] => {
  return [
    createSegmentPredicate(selectedConnectionThreshold),
    createSegmentPredicate(selectedConnectionThreshold),
  ];
};

const createItineraryPredicate = (
  filterRequest: string[],
  tripType: string
): ItineraryPredicate => {
  if (!filterRequest) {
    return alwaysTrue;
  }
  const segmentPredicates =
    tripType === 'ROUND_TRIP'
      ? createRoundTripSegmentPredicates(filterRequest[0])
      : filterRequest.map(createSegmentPredicate);
  return ({ legs }) =>
    legs.every(
      (leg, index) =>
        segmentPredicates[index] && segmentPredicates[index](leg.segments[0])
    );
};

export const usePredicate = (): PredicateContextType =>
  useContext(PredicateContext);

export default function PredicateProvider({
  children,
  transportType,
}: PredicateProviderProps) {
  const maxStopsFilterState = useFilterState();
  const { tripType = 'ONE_WAY' } = usePerformedSearchRequest() || {};
  const airportsPredicate = createItineraryPredicate(
    maxStopsFilterState[transportType],
    tripType
  );
  return (
    <PredicateContext.Provider value={airportsPredicate}>
      {children}
    </PredicateContext.Provider>
  );
}
